import { DocumentFile, SingleDocument, Tag } from "interfaces/content";
import flatten from "lodash/flatten";
import Fuse from "fuse.js";
import documentDescriptions from "components/content/Document/DocumentDescriptions";
import { isDocumentFile } from "components/content/Document/Document";

/**
 * @desc getDocumentTags Fn creates a list of all unique tags included in the provided documents
 */
export const getDocumentTags = (documents: SingleDocument[]): Tag[] =>
    flatten(
        documents.map((document) => (document.documentTags ? document.documentTags : []))
    ).reduce((tags: Tag[], currentTag) => {
        const duplicateTag = tags.some((tag) => tag.id === currentTag.id);

        return duplicateTag ? tags : [...tags, currentTag];
    }, []);

/**
 * @desc filterDocumentsWithSearchTerm Fn filters documents if
 *        the given search term is part of the document's title
 */
export const filterDocumentsWithSearchTerm = (
    documents: SingleDocument[],
    searchTerm: string
): SingleDocument[] =>
    documents.filter((document) =>
        document.title.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())
    );

/**
 * @desc filterDocumentsWithTags Fn filters documents based on the given list of tags
 */
export const filterDocumentsWithTags = (
    documents: SingleDocument[],
    tagFilters: Tag[]
): SingleDocument[] => {
    if (tagFilters.length < 1) {
        return documents;
    }

    const documentsWithTags = documents.filter((document) => document.documentTags);

    return documentsWithTags.reduce((documentsWithTags: DocumentFile[], document) => {
        const documentWithTags = document.documentTags!.some((tag) =>
            tagFilters.some((tagFilter) => tagFilter.id === tag.id)
        );

        return documentWithTags ? [...documentsWithTags, document] : documentsWithTags;
    }, []);
};

/**
 *  @desc filterDocuments Fn filters documents based on given search term and tag-filters list
 */
export const filterDocuments = (
    allDocuments: SingleDocument[],
    searchTerm: string,
    tagFilters: Tag[]
) => {
    const docs = allDocuments
        .filter(
            (doc) =>
                "Dokumenttiarkisto" !== doc.containerId &&
                "Arkistoidut tiedonvaihdon ohjeet (EDIEL)" !== doc.containerId
        )
        .map((doc) => {
            const description = documentDescriptions.find(
                (d) => isDocumentFile(doc) && d.mFileIds.includes(doc.mFilesDocument.file.id)
            );

            return {
                ...doc,
                excerpt: description?.excerpt,
                description: description?.description,
                locale: description?.locale,
            };
        });

    const fuseOptions = {
        isCaseSensitive: false,
        includeScore: true,
        threshold: 0.15,
        ignoreLocation: true,
        keys: ["description", "title", "excerpt"],
    };

    const fuse = new Fuse(docs, fuseOptions);
    const results: SingleDocument[] =
        searchTerm && searchTerm.trim().length >= 0
            ? fuse.search(searchTerm).map((result) => result.item)
            : docs;

    return filterDocumentsWithTags(results, tagFilters);
};
